import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { GLASS, TYPE } from "@/lib/glass";
import { renderLightCards } from "@/lib/lightCard";

const ORIGIN = process.env.REACT_APP_SITE_ORIGIN;
const SHARE_TEXT = "I have entered the light \u2014 Prism Pixel\u2122";

const action =
  "min-h-[44px] bg-transparent px-3 font-light tracking-[0.08em] text-[#F5F5F7]/70 transition-colors duration-500 hover:text-[#84DFF4]";

export default function LightCard({ seed }) {
  const [cards, setCards] = useState(null);
  const [url, setUrl] = useState("");
  const [note, setNote] = useState("");

  useEffect(() => {
    let alive = true;
    renderLightCards(seed).then((c) => alive && setCards(c));
    return () => {
      alive = false;
    };
  }, [seed]);

  useEffect(() => {
    if (!cards) return undefined;
    const u = URL.createObjectURL(cards.portrait);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [cards]);

  useEffect(() => {
    if (!note) return undefined;
    const t = setTimeout(() => setNote(""), 2400);
    return () => clearTimeout(t);
  }, [note]);

  if (!cards || !url) return null;

  const file = () => new File([cards.portrait], "prism-pixel-light.png", { type: "image/png" });
  const canShare = typeof navigator.share === "function" && navigator.canShare?.({ files: [file()] });

  const save = () => {
    const a = document.createElement("a");
    a.href = url;
    a.download = "prism-pixel-light.png";
    a.click();
  };
  const share = async () => {
    try {
      await navigator.share({ files: [file()], title: SHARE_TEXT, text: SHARE_TEXT, url: ORIGIN });
    } catch {
      /* dismissed */
    }
  };
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(`${ORIGIN}/`);
      setNote("Link copied");
    } catch {
      setNote("Could not copy");
    }
  };

  return (
    <motion.figure
      data-testid="light-card"
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 1.6, delay: 1.6, ease: GLASS.settle }}
      className="mx-auto mt-14 flex w-full max-w-[22rem] flex-col items-center"
    >
      <img
        data-testid="light-card-image"
        src={url}
        alt={`Your light card: ${SHARE_TEXT}, ${cards.dateLabel}`}
        width={1080}
        height={1920}
        className="block h-auto w-[11.5rem] border border-[#8E8E93]/30 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.95),0_0_60px_-20px_rgba(132,223,244,0.18)]"
      />
      <figcaption
        className="mt-5 text-center font-light text-[#F5F5F7]/70"
        style={{ fontSize: TYPE.small, letterSpacing: "0.04em" }}
      >
        Your light, from the moment you entered.
      </figcaption>
      <div className="mt-3 flex flex-wrap items-center justify-center" style={{ fontSize: TYPE.small }}>
        <button type="button" data-testid="light-card-save" onClick={save} className={action}>
          Save
        </button>
        {canShare && (
          <>
            <span aria-hidden="true" className="h-px w-4 bg-[#8E8E93]/40" />
            <button type="button" data-testid="light-card-share" onClick={share} className={action}>
              Share
            </button>
          </>
        )}
        <span aria-hidden="true" className="h-px w-4 bg-[#8E8E93]/40" />
        <button type="button" data-testid="light-card-copy" onClick={copy} className={action}>
          Copy link
        </button>
      </div>
      <p
        data-testid="light-card-note"
        role="status"
        aria-live="polite"
        className="mt-2 min-h-[1.25rem] font-light text-[#84DFF4]"
        style={{ fontSize: TYPE.small, letterSpacing: "0.04em" }}
      >
        {note}
      </p>
    </motion.figure>
  );
}
